/* eslint-disable prettier/prettier */

/**
 * Mirrors the server's denial reasons (src/libs/accountaccess.lib.ts and the
 * social auth callback). The server redirects to
 *   /login/error?reason=<key>&provider=<google|linkedin>&email=<...>&deletedat=<iso>
 * so any key added on the server must be added here too, or the user sees the
 * generic fallback.
 */
export type AuthErrorReason =
	| 'account_deleted'
	| 'account_suspended'
	| 'account_inactive'
	| 'account_locked'
	| 'email_not_provided'
	| 'auth_failed'
	| 'no_user'
	| 'token_generation_failed'
	| 'missing_tokens'
	| 'profile_fetch_failed';

export interface AuthErrorCopy {
	title: string;
	/** Shown under the title. Kept free of identity detail — that is added separately. */
	description: string;
	/** What the person can actually do next. */
	action: string;
	/** Deleted accounts are final; the rest are worth retrying or escalating. */
	canRetry: boolean;
}

export const AUTH_ERROR_COPY: Record<AuthErrorReason, AuthErrorCopy> = {
	account_deleted: {
		title: 'This account has been deleted',
		description:
			'The IOCM account linked to this sign-in was deleted and can no longer be used to log in.',
		action: 'If you believe this was a mistake, contact your administrator or IOCM support with the details below.',
		canRetry: false,
	},
	account_suspended: {
		title: 'This account is suspended',
		description:
			'Access to this IOCM account has been suspended by an administrator.',
		action: 'Contact your administrator to have it reinstated.',
		canRetry: false,
	},
	account_inactive: {
		title: 'This account is inactive',
		description: 'This IOCM account is no longer active.',
		action: 'Contact your administrator to reactivate it.',
		canRetry: false,
	},
	account_locked: {
		title: 'This account is temporarily locked',
		description:
			'Too many failed sign-in attempts locked this account for a short period.',
		action: 'Wait a few minutes and try again, or reset your password.',
		canRetry: true,
	},
	email_not_provided: {
		title: 'No email address was shared',
		description:
			'Your provider completed sign-in but did not share an email address, which IOCM needs to identify your account.',
		action: 'Try again and allow access to your email address, or sign in with your email and password.',
		canRetry: true,
	},
	auth_failed: {
		title: "Sign-in didn't complete",
		description: 'We could not complete sign-in with your provider.',
		action: 'Please try again.',
		canRetry: true,
	},
	no_user: {
		title: 'No account was found',
		description: 'Sign-in completed but no matching IOCM account was returned.',
		action: 'Please try again, or contact support if this keeps happening.',
		canRetry: true,
	},
	token_generation_failed: {
		title: "Sign-in didn't complete",
		description: 'Your identity was verified but we could not start your session.',
		action: 'Please try again.',
		canRetry: true,
	},
	missing_tokens: {
		title: "Sign-in didn't complete",
		description: 'The sign-in response was missing its authentication tokens.',
		action: 'Please try again.',
		canRetry: true,
	},
	profile_fetch_failed: {
		title: 'We could not load your profile',
		description: 'Sign-in succeeded but your profile could not be retrieved.',
		action: 'Please try again in a moment.',
		canRetry: true,
	},
};

export const FALLBACK_AUTH_ERROR: AuthErrorCopy = {
	title: "Sign-in didn't complete",
	description: 'Something went wrong while signing you in.',
	action: 'Please try again, or contact support if this keeps happening.',
	canRetry: true,
};

export const isAuthErrorReason = (value: string | null): value is AuthErrorReason =>
	value !== null && Object.prototype.hasOwnProperty.call(AUTH_ERROR_COPY, value);

export const getAuthErrorCopy = (reason: string | null): AuthErrorCopy =>
	isAuthErrorReason(reason) ? AUTH_ERROR_COPY[reason] : FALLBACK_AUTH_ERROR;

export const PROVIDER_LABELS: Record<string, string> = {
	google: 'Google',
	linkedin: 'LinkedIn',
	email: 'email and password',
};

export const getProviderLabel = (provider: string | null): string =>
	(provider && PROVIDER_LABELS[provider.toLowerCase()]) || 'your provider';

/** Clears every credential this app stores. Used whenever a session is refused. */
export const clearStoredCredentials = (): void => {
	try {
		localStorage.removeItem('token');
		localStorage.removeItem('refreshtoken');
		localStorage.removeItem('user');
	} catch (error) {
		console.error('Failed to clear stored credentials', error);
	}
};
